import React, { useState } from "react";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../../design/dialog";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
  Description,
} from "../../../../design/fieldset";
import { Select } from "../../../../design/select";
import { Input } from "../../../../design/input";
import { useAttorney } from "../../../../contexts/AttorneyContext";
import { useCase } from "../../../../contexts/CaseContext";
import { useAttorneyDialog } from "../../../../providers/AttorneyDialogProvider";
import {
  AttorneyRole,
  ATTORNEY_ROLE_LABELS,
  CaseAttorney,
  getAttorneyDisplayName,
} from "@visitproof/types";
import { Listbox, ListboxLabel, ListboxOption } from "../../../../design/listbox";
import { Checkbox, CheckboxField, CheckboxGroup } from "../../../../design/checkbox";
import { recordWebTelemetry } from "../../../../utils/telemetry";

interface Props {
  caseId: string;
  existingAssignments: CaseAttorney[];
  onAssigned: (assignment: CaseAttorney) => void;
}

const ROLE_OPTIONS: { value: AttorneyRole; label: string }[] = [
  { value: "visiting_parent_attorney", label: ATTORNEY_ROLE_LABELS.visiting_parent_attorney },
  { value: "custodial_parent_attorney", label: ATTORNEY_ROLE_LABELS.custodial_parent_attorney },
  { value: "petitioner_attorney", label: ATTORNEY_ROLE_LABELS.petitioner_attorney },
  { value: "respondent_attorney", label: ATTORNEY_ROLE_LABELS.respondent_attorney },
  { value: "childs_attorney", label: ATTORNEY_ROLE_LABELS.childs_attorney },
  { value: "gal", label: ATTORNEY_ROLE_LABELS.gal },
  { value: "minors_counsel", label: ATTORNEY_ROLE_LABELS.minors_counsel },
  { value: "casa", label: ATTORNEY_ROLE_LABELS.casa },
  { value: "mediator", label: ATTORNEY_ROLE_LABELS.mediator },
  { value: "other", label: ATTORNEY_ROLE_LABELS.other },
];

interface Errors {
  attorneyId?: string;
  role?: string;
  roleLabel?: string;
  representedParentId?: string;
  submit?: string;
}

const AssignAttorneyButton: React.FC<Props> = ({
  caseId,
  existingAssignments,
  onAssigned,
}) => {
  const { attorneys, assignAttorneyToCase, getCaseAttorneys } = useAttorney();
  const { parents, childList } = useCase();
  const { openCreateDialog } = useAttorneyDialog();

  const [isOpen, setIsOpen] = useState(false);
  const [attorneyId, setAttorneyId] = useState<string>("");
  const [role, setRole] = useState<AttorneyRole>("visiting_parent_attorney");
  const [roleLabel, setRoleLabel] = useState("");
  const [representedParentId, setRepresentedParentId] = useState<string>("");
  const [representedChildIds, setRepresentedChildIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [showCreateAttorney, setShowCreateAttorney] = useState(false);

  // Filter to only active attorneys not already assigned in this role
  const availableAttorneys = attorneys.filter((a) => a.status === "active");

  const isParentRole =
    role === "visiting_parent_attorney" ||
    role === "custodial_parent_attorney" ||
    role === "petitioner_attorney" ||
    role === "respondent_attorney";
  const isChildRole =
    role === "childs_attorney" ||
    role === "gal" ||
    role === "minors_counsel" ||
    role === "casa";

  const handleOpen = () => {
    setAttorneyId("");
    setRole("visiting_parent_attorney");
    setRoleLabel("");
    setRepresentedParentId("");
    setRepresentedChildIds([]);
    setErrors({});
    setShowCreateAttorney(false);
    setIsOpen(true);
  };

  const handleCreateNewAttorney = () => {
    // Close this dialog and open the create attorney dialog
    setIsOpen(false);
    openCreateDialog({
      onAttorneyCreated: (newAttorneyId) => {
        // Reopen the assign dialog with the newly created attorney selected
        setAttorneyId(newAttorneyId);
        setIsOpen(true);
      },
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const assignmentId = await assignAttorneyToCase({
        caseId,
        attorneyId,
        role,
        roleLabel: role === "other" ? roleLabel : undefined,
        representedParentId: isParentRole ? representedParentId || undefined : undefined,
        representedChildIds: isChildRole && representedChildIds.length > 0 ? representedChildIds : undefined,
        isActive: true,
      });

      // Fetch the created assignment to return it
      const assignments = await getCaseAttorneys(caseId);
      const newAssignment = assignments.find((a) => a.id === assignmentId);
      if (newAssignment) {
        onAssigned(newAssignment);
      }

      setIsOpen(false);
    } catch (error: any) {
      // Surface the failure instead of silently doing nothing.
      console.error("Error assigning attorney:", error);
      const msg =
        error?.code === "permission-denied"
          ? "You don't have permission to assign an attorney. Please contact support."
          : error?.message || "Couldn't assign the attorney. Please try again.";
      setErrors((prev) => ({ ...prev, submit: msg }));
      recordWebTelemetry("error", "assign_attorney_failed", {
        code: error?.code || "unknown",
        message: error?.message?.slice(0, 200),
        caseId,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (!attorneyId) {
      newErrors.attorneyId = "Please select an attorney";
    }

    if (!role) {
      newErrors.role = "Please select a role";
    }

    if (role === "other" && !roleLabel.trim()) {
      newErrors.roleLabel = "Please specify the role";
    }

    if (isParentRole && !representedParentId) {
      newErrors.representedParentId = "Please select which parent this attorney represents";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChildToggle = (childId: string, checked: boolean) => {
    if (checked) {
      setRepresentedChildIds((prev) => [...prev, childId]);
    } else {
      setRepresentedChildIds((prev) => prev.filter((id) => id !== childId));
    }
  };

  return (
    <>
      <Button type="button" onClick={handleOpen}>
        Assign Attorney
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} size="2xl">
        <DialogTitle>Assign Attorney to Case</DialogTitle>
        <DialogDescription>
          Select an attorney and specify their role in this case. You can also define which parent or children they represent.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {/* Attorney Selection */}
              <Field>
                <div className="flex items-center justify-between mb-2">
                  <Label>Attorney <span className="text-red-500">*</span></Label>
                  <Button
                    type="button"
                    outline
                    onClick={handleCreateNewAttorney}
                    className="text-xs"
                  >
                    + Add New Attorney
                  </Button>
                </div>
                <Listbox
                  value={attorneyId || undefined}
                  onChange={setAttorneyId}
                  placeholder="Select an attorney..."
                  invalid={!!errors.attorneyId}
                >
                  {availableAttorneys.map((attorney) => (
                    <ListboxOption key={attorney.id} value={attorney.id}>
                      <ListboxLabel>
                        {getAttorneyDisplayName(attorney)}
                        {attorney.firmName && (
                          <span className="text-zinc-500"> — {attorney.firmName}</span>
                        )}
                      </ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
                {errors.attorneyId && (
                  <ErrorMessage>{errors.attorneyId}</ErrorMessage>
                )}
                {availableAttorneys.length === 0 && (
                  <Description>
                    No attorneys available. Click "Add New Attorney" above to create one.
                  </Description>
                )}
              </Field>

              {/* Role Selection */}
              <Field>
                <Label>Role <span className="text-red-500">*</span></Label>
                <Description>What role does this attorney play in the case?</Description>
                <Select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value as AttorneyRole);
                    // Clear parent/child selections when role changes
                    setRepresentedParentId("");
                    setRepresentedChildIds([]);
                  }}
                  invalid={!!errors.role}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
              </Field>

              {/* Custom Role Label (for "other") */}
              {role === "other" && (
                <Field>
                  <Label>Role Description <span className="text-red-500">*</span></Label>
                  <Description>Specify the custom role for this attorney</Description>
                  <Input
                    type="text"
                    value={roleLabel}
                    onChange={(e) => setRoleLabel(e.target.value)}
                    placeholder="e.g., Co-counsel, Expert witness..."
                    invalid={!!errors.roleLabel}
                  />
                  {errors.roleLabel && (
                    <ErrorMessage>{errors.roleLabel}</ErrorMessage>
                  )}
                </Field>
              )}

              {/* Divider for representation section */}
              {(isParentRole && parents.length > 0) || (isChildRole && childList.length > 0) ? (
                <div className="pt-4 mt-4 border-t border-zinc-200 dark:border-zinc-700">
                  <p className="mb-4 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Representation Details
                  </p>
                </div>
              ) : null}

              {/* Parent Selection (for petitioner/respondent attorneys) */}
              {isParentRole && parents.length > 0 && (
                <Field>
                  <Label>Parent Representation <span className="text-red-500">*</span></Label>
                  <Description>
                    Which parent does this attorney represent?
                  </Description>
                  <Select
                    value={representedParentId}
                    onChange={(e) => setRepresentedParentId(e.target.value)}
                    invalid={!!errors.representedParentId}
                  >
                    <option value="">Select a parent...</option>
                    {parents.map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.firstName} {parent.lastName}
                        {parent.role ? ` (${parent.role})` : ""}
                      </option>
                    ))}
                  </Select>
                  {errors.representedParentId && (
                    <ErrorMessage>{errors.representedParentId}</ErrorMessage>
                  )}
                </Field>
              )}

              {/* Child Selection (for GAL, Minor's Counsel, CASA) */}
              {isChildRole && childList.length > 0 && (
                <Field>
                  <Label>Child Representation</Label>
                  <Description>
                    Which child(ren) does this attorney/advocate represent? (Optional - select none to represent all children)
                  </Description>
                  <div className="p-4 mt-2 space-y-2 border rounded-lg border-zinc-300 dark:border-zinc-700">
                    {childList.map((child) => (
                      <CheckboxField key={child.id}>
                        <Checkbox
                          checked={representedChildIds.includes(child.id)}
                          onChange={(checked) => handleChildToggle(child.id, checked)}
                        />
                        <Label>
                          {child.firstName} {child.lastName}
                        </Label>
                      </CheckboxField>
                    ))}
                  </div>
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        {errors.submit && (
          <div className="px-6">
            <ErrorMessage>{errors.submit}</ErrorMessage>
          </div>
        )}
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Assigning..." : "Assign Attorney"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignAttorneyButton;
